import { useEffect, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  useAcceptInvitation,
  useInvitationPreview,
  useMe,
} from '@fluxter/shared';
import { Loader2, XCircle } from 'lucide-react';
import { tokenStore } from '@/lib/api';
import { stashPostLoginReturnUrl } from '@/lib/post-login-redirect';
import { AuthHeader, AuthShell } from '@/components/AuthShell';
import { Button } from '@/components/ui/button';

interface InvitationPreview {
  data?: {
    attributes?: {
      email?: string;
      role?: 'admin' | 'member';
      inviter_name?: string | null;
      inviter_email?: string | null;
      workspace_name?: string | null;
      workspace_slug?: string | null;
      is_pending?: boolean;
      is_expired?: boolean;
    };
  };
}

interface MeData {
  data?: { id?: string; attributes?: { email?: string } };
}

interface AcceptResult {
  data?: { attributes?: { workspace_slug?: string | null } };
}

/**
 * Public-facing invitation accept page at `/i/{token}`. Three states:
 *
 *   1. Token loads → show workspace + role + inviter, plus an
 *      "Accept" button (or "Sign in to accept" if unauthenticated).
 *   2. Token is expired / already accepted / not found → show an
 *      explanatory state and link back to home.
 *   3. Email mismatch (signed-in user's email ≠ invitation's email)
 *      → render explicit warning before letting them attempt accept.
 *
 * Sign-in flow: stashes its own URL via `stashPostLoginReturnUrl`
 * before redirecting to login; LoginPage / AuthCallbackPage call
 * `consumePostLoginReturnUrl` after a successful sign-in to come
 * back here. Allow-list scoping keeps the redirect from being an
 * open-redirect surface.
 */

export const InvitationAcceptPage = () => {
  const { invitationToken } = useParams({ from: '/i/$invitationToken' });
  const navigate = useNavigate();

  const preview = useInvitationPreview(invitationToken);
  const me = useMe();
  const accept = useAcceptInvitation();

  const [error, setError] = useState<string | null>(null);

  const previewAttrs = (preview.data as InvitationPreview | undefined)?.data?.attributes;
  const meAttrs = (me.data as MeData | undefined)?.data?.attributes;
  const meEmail = meAttrs?.email ?? null;
  const isAuthenticated = Boolean((me.data as MeData | undefined)?.data?.id);

  // Surface preview errors clearly. The hook's `retry: false` keeps
  // the request from looping on 410.
  const previewError = preview.error
    ? extractErrorMessage(preview.error)
    : null;

  const emailMismatch =
    isAuthenticated && previewAttrs?.email && meEmail
      ? previewAttrs.email.toLowerCase() !== meEmail.toLowerCase()
      : false;

  // Auto-accept on mount when authenticated, the preview loaded
  // cleanly, and the email matches. Skip if we're still in error or
  // already accepted state, or while either query is loading.
  const [autoAttempted, setAutoAttempted] = useState(false);
  useEffect(() => {
    if (autoAttempted) return;
    if (preview.isLoading || me.isLoading) return;
    if (!isAuthenticated || !previewAttrs || emailMismatch) return;
    if (previewError) return;
    setAutoAttempted(true);
    void handleAccept();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    preview.isLoading,
    me.isLoading,
    previewAttrs,
    emailMismatch,
    previewError,
    autoAttempted,
  ]);

  const handleSignIn = async (): Promise<void> => {
    // Stash the return URL so the post-login redirect lands back here.
    stashPostLoginReturnUrl(`/i/${invitationToken}`);
    await navigate({ to: '/login' });
  };

  const handleAccept = async (): Promise<void> => {
    setError(null);
    try {
      const result = (await accept.mutateAsync(invitationToken)) as AcceptResult;
      const slug = result?.data?.attributes?.workspace_slug ?? previewAttrs?.workspace_slug;
      if (slug) {
        await navigate({ to: '/w/$workspaceSlug', params: { workspaceSlug: slug } });
      } else {
        // Fall back to the home redirect chain if the slug somehow
        // didn't make it through.
        await navigate({ to: '/' });
      }
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  const handleSignOut = async (): Promise<void> => {
    await tokenStore.clear();
    await navigate({ to: '/login' });
  };

  return (
    <AuthShell width="md" header={<AuthHeader title="Workspace invitation" />}>
      {preview.isLoading ? (
        <p className="text-foreground-muted flex items-center justify-center gap-2 text-[12.5px]">
          <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
          Loading invitation…
        </p>
      ) : previewError ? (
        <ErrorState message={previewError} onHome={() => void navigate({ to: '/' })} />
      ) : !previewAttrs ? (
        <ErrorState
          message="This invitation could not be loaded."
          onHome={() => void navigate({ to: '/' })}
        />
      ) : (
        <div className="space-y-5">
          <p className="text-foreground text-center text-[13px] leading-relaxed">
            <span className="font-medium">
              {previewAttrs.inviter_name ?? previewAttrs.inviter_email ?? 'Someone'}
            </span>{' '}
            invited <span className="font-medium">{previewAttrs.email}</span> to
            join{' '}
            <span className="font-medium">{previewAttrs.workspace_name}</span> as a{' '}
            <span className="font-medium">{previewAttrs.role}</span>.
          </p>

          {!isAuthenticated ? (
            <Button type="button" className="w-full" onClick={() => void handleSignIn()}>
              Sign in to accept
            </Button>
          ) : emailMismatch ? (
            <div className="space-y-3">
              <div className="border-destructive/40 bg-destructive/5 rounded-md border p-3 text-[12.5px]">
                <p className="text-destructive font-medium">Email mismatch</p>
                <p className="text-foreground-muted mt-1 text-[11.5px]">
                  You're signed in as <span className="font-medium">{meEmail}</span>,
                  but this invitation was sent to{' '}
                  <span className="font-medium">{previewAttrs.email}</span>. Sign out and
                  sign back in with the right account to accept.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => void handleSignOut()}
              >
                Sign out
              </Button>
            </div>
          ) : accept.isPending ? (
            <p className="text-foreground-muted flex items-center justify-center gap-2 text-[12.5px]">
              <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
              Joining workspace…
            </p>
          ) : (
            <Button type="button" className="w-full" onClick={() => void handleAccept()}>
              Accept invitation
            </Button>
          )}

          {error ? (
            <p className="text-destructive text-center text-[12.5px]" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      )}
    </AuthShell>
  );
};

const ErrorState = ({
  message,
  onHome,
}: {
  message: string;
  onHome: () => void;
}) => (
  <div className="space-y-4 text-center">
    <XCircle className="text-foreground-faint mx-auto size-10" aria-hidden="true" />
    <p className="text-foreground text-[13px]">{message}</p>
    <Button type="button" variant="outline" onClick={onHome}>
      Go home
    </Button>
  </div>
);

const extractErrorMessage = (e: unknown): string => {
  if (e instanceof Error) return e.message;
  if (e && typeof e === 'object' && 'message' in e) {
    return String((e as { message: unknown }).message);
  }
  return 'Something went wrong with this invitation.';
};

